import React, { useState, useEffect, useRef, useCallback } from "react";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { Mic, MicOff, Square } from "lucide-react";

interface VoiceRecorderProps {
  onTranscriptChange: (text: string) => void;
  initialText?: string;
  disabled?: boolean;
}

const CHUNK_INTERVAL_MS = 25000; // Restart recognition every 25s to prevent browser freeze

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({ onTranscriptChange, initialText = "", disabled = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const bufferRef = useRef(initialText);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const chunkTimerRef = useRef<NodeJS.Timeout | null>(null);

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  // Flush current transcript into the buffer and restart recognition
  const flushAndRestart = useCallback(() => {
    if (transcript) {
      bufferRef.current = bufferRef.current
        ? `${bufferRef.current} ${transcript}`
        : transcript;
      onTranscriptChange(bufferRef.current);
      resetTranscript();
    }
    if (isRecording) {
      SpeechRecognition.startListening({ continuous: true });
    }
  }, [transcript, isRecording, resetTranscript, onTranscriptChange]);

  // Set up chunking interval while recording
  useEffect(() => {
    if (isRecording) {
      chunkTimerRef.current = setInterval(flushAndRestart, CHUNK_INTERVAL_MS);
    }
    return () => {
      if (chunkTimerRef.current) clearInterval(chunkTimerRef.current);
    };
  }, [isRecording, flushAndRestart]);

  // Update parent with buffer + live transcript
  useEffect(() => {
    if (transcript && isRecording) {
      const fullText = bufferRef.current
        ? `${bufferRef.current} ${transcript}`
        : transcript;
      onTranscriptChange(fullText);
    }
  }, [transcript, isRecording, onTranscriptChange]);

  // Elapsed time counter
  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setElapsedSeconds((s) => s + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRecording]);

  const startRecording = () => {
    if (!browserSupportsSpeechRecognition) return;
    bufferRef.current = initialText;
    setElapsedSeconds(0);
    resetTranscript();
    setIsRecording(true);
    SpeechRecognition.startListening({ continuous: true });
  };

  const stopRecording = () => {
    SpeechRecognition.stopListening();
    // Final flush
    if (transcript) {
      bufferRef.current = bufferRef.current
        ? `${bufferRef.current} ${transcript}`
        : transcript;
      onTranscriptChange(bufferRef.current);
      resetTranscript();
    }
    setIsRecording(false);
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const wordCount = (bufferRef.current + (transcript ? ` ${transcript}` : "")).trim().split(/\s+/).filter(Boolean).length;

  if (!browserSupportsSpeechRecognition) return null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      {!isRecording ? (
        <button
          onClick={startRecording}
          disabled={disabled}
          style={{
            display: "flex", alignItems: "center", gap: "6px",
            padding: "8px 16px", borderRadius: "8px",
            border: "1px solid #D1D5DB", backgroundColor: "#FFFFFF",
            fontSize: "13px", color: "#374151", cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <Mic size={16} color="#6366F1" />
          Record Voice
        </button>
      ) : (
        <>
          <button
            onClick={stopRecording}
            style={{
              display: "flex", alignItems: "center", gap: "6px",
              padding: "8px 16px", borderRadius: "8px",
              border: "1px solid #EF4444", backgroundColor: "#FEF2F2",
              fontSize: "13px", color: "#EF4444", cursor: "pointer",
            }}
          >
            <Square size={14} fill="#EF4444" />
            Stop
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{
              display: "inline-block", width: "8px", height: "8px",
              borderRadius: "50%", backgroundColor: "#EF4444",
              animation: "pulse 1.5s ease-in-out infinite",
            }} />
            <span style={{ fontSize: "13px", color: "#6B7280", fontVariantNumeric: "tabular-nums" }}>
              {formatTime(elapsedSeconds)}
            </span>
          </div>
        </>
      )}
      {wordCount > 0 && (
        <span style={{ fontSize: "12px", color: "#9CA3AF" }}>
          {wordCount} word{wordCount !== 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
};

export default VoiceRecorder;
